
import React, { useState, useRef, useEffect } from 'react';
import { AudioFileState, RecordingMetadata, YouTubeReference } from '../types';
import { Recorder } from './studio/Recorder';
import { createPortal } from 'react-dom';
import { registerURLManual } from '../services/memoryService';
import { useGuitarMatch } from '../context/GuitarMatchContext';
import { analyzeAudioQuality, AudioQualityReport } from '../services/audioQualityService';
import { YouTubePlayer } from './youtube/YouTubePlayer';

const QualityBadge: React.FC<{ report: AudioQualityReport | null, isScanning: boolean }> = ({ report, isScanning }) => {
  if (isScanning) return <span className="text-[7px] font-black text-white/20 animate-pulse uppercase">Scanning Core...</span>;
  if (!report) return null;
  const styles = { OK: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', WARN: 'bg-orange-500/10 text-orange-400 border-orange-500/20', REJECT: 'bg-red-500/10 text-red-400 border-red-500/20' };
  return <div className={`text-[8px] font-black px-3 py-1 rounded-full border ${styles[report.status]}`}>{report.status === 'OK' ? 'SIGNAL OPTIMAL' : (report.message || 'CHECK HARDWARE')}</div>;
};

export const AudioUploader: React.FC<{
  id?: string; label: string; onFileSelect: (file: File | YouTubeReference, m?: RecordingMetadata) => void;
  color: 'success' | 'accent' | 'gold'; defaultMode?: 'audio' | 'video'; externalFile?: File | null; 
  isTeacher?: boolean; masterReferenceFile?: File | null; youtubeRef?: YouTubeReference | null;
}> = ({ label, onFileSelect, externalFile, isTeacher = false, masterReferenceFile, youtubeRef }) => {
  const { setYoutubeMasterRef, manualGrid } = useGuitarMatch();
  const [isRecordingModalOpen, setIsRecordingModalOpen] = useState(false);
  const [audioState, setAudioState] = useState<AudioFileState>({ file: null, url: null });
  const [qualityReport, setQualityReport] = useState<AudioQualityReport | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isStaged, setIsStaged] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (externalFile) {
        const url = registerURLManual(URL.createObjectURL(externalFile));
        setAudioState({ file: externalFile, url });
        setIsStaged(false);
    } else if (!youtubeRef) {
        setAudioState({ file: null, url: null });
    }
  }, [externalFile, youtubeRef]);

  const handlePreScan = async (file: File) => {
      setIsScanning(true);
      try {
          const report = await analyzeAudioQuality(file);
          setQualityReport(report);
          if (report.status === 'REJECT') return; 
          const url = registerURLManual(URL.createObjectURL(file));
          setAudioState({ file, url });
          if (!isTeacher) setIsStaged(true);
          else onFileSelect(file);
      } finally {
          setIsScanning(false);
      }
  };

  const confirmMatch = () => {
    if (audioState.file && !isLaunching) {
        setIsLaunching(true);
        // On donne un peu d'air au thread UI avant l'appel lourd
        requestAnimationFrame(() => {
            onFileSelect(audioState.file!, audioState.metadata);
            // Sécurité : On réinitialise l'état local après 1s pour éviter le lock UI
            setTimeout(() => setIsLaunching(false), 1000);
        });
    }
  };

  const handleClear = () => {
      if (isTeacher) {
          setYoutubeMasterRef?.(null);
          onFileSelect(null as any); 
      }
  };

  return (
    <div className="flex flex-col h-full w-full gap-4 group/uploader">
      <div className="flex items-center justify-between px-6 py-3 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-xl">
        <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 group-hover/uploader:text-white transition-colors">{label}</label>
        <QualityBadge report={qualityReport} isScanning={isScanning} />
      </div>
      
      <div className={`flex-1 relative overflow-hidden rounded-[48px] border transition-all duration-700 bg-black/40 ${ (audioState.file || youtubeRef) ? 'border-white/20 shadow-2xl' : 'border-white/5 hover:border-white/10'}`}>
        
        {isTeacher && youtubeRef ? (
            <div className="absolute inset-0 flex flex-col animate-fade-in">
                <YouTubePlayer videoId={youtubeRef.videoId} startSec={youtubeRef.startSec} className="h-full w-full opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#020408] via-[#020408]/20 to-transparent p-10 flex flex-col justify-end gap-4 pointer-events-none">
                    <div className="space-y-1">
                        <span className="text-[9px] font-black text-red-500 uppercase tracking-[0.4em] italic">MASTER: YouTube</span>
                        <h4 className="text-2xl font-black text-white uppercase italic tracking-tighter truncate">{youtubeRef.title}</h4>
                        <div className="flex gap-4">
                            <span className="text-[9px] font-mono text-slate-500">START: {youtubeRef.startSec}s | END: {youtubeRef.endSec}s</span>
                        </div>
                    </div>
                    <button onClick={handleClear} className="pointer-events-auto self-start px-6 py-2 rounded-full bg-white/5 border border-white/10 text-white font-black uppercase text-[8px] hover:bg-red-600 transition-all">Clear Master</button>
                </div>
            </div>
        ) : audioState.file ? (
            <div className="absolute inset-0 flex flex-col animate-fade-in">
                {audioState.file.type.includes('video') ? (
                    <video src={audioState.url || ""} className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale-[0.2]" muted loop autoPlay playsInline />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-white/5 to-transparent text-6xl opacity-10">🎸</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#020408] via-[#020408]/40 to-transparent p-10 flex flex-col justify-end gap-6">
                    <h4 className="text-2xl font-black text-white uppercase italic tracking-tighter truncate">{audioState.file.name}</h4>
                    <div className="flex gap-3">
                        {isStaged ? (
                            <button onClick={confirmMatch} disabled={isLaunching || qualityReport?.status === 'REJECT'} className={`flex-1 py-5 rounded-2xl text-white font-black uppercase text-xs tracking-[0.4em] transition-all ${isLaunching ? 'bg-slate-800 animate-pulse' : 'bg-smart-accent shadow-[0_0_40px_rgba(255,98,97,0.4)]'}`}>
                                {isLaunching ? 'Lancement... 🧬' : 'Match ! ⚡'}
                            </button>
                        ) : (
                            <button onClick={() => setIsRecordingModalOpen(true)} className="flex-1 py-5 rounded-2xl bg-white text-black font-black uppercase text-[9px] shadow-xl">Refaire</button>
                        )}
                        <button onClick={() => inputRef.current?.click()} className="px-6 py-5 rounded-2xl bg-white/5 border border-white/10 text-white font-black uppercase text-[9px]">Changer</button>
                    </div>
                </div>
            </div>
        ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 gap-8">
                <button onClick={() => setIsRecordingModalOpen(true)} className="group/rec relative w-32 h-32 md:w-44 md:h-44 flex items-center justify-center transition-all duration-700 hover:scale-105 active:scale-90">
                    <div className="absolute inset-0 rounded-full border border-white/10 group-hover/rec:border-white/30 transition-all"></div>
                    <div className="w-full h-full rounded-full bg-white/[0.03] backdrop-blur-3xl flex flex-col items-center justify-center gap-3">
                        <div className="w-4 h-4 rounded-full bg-red-600 animate-pulse shadow-[0_0_20px_red]"></div>
                        <span className="text-[10px] font-black text-white uppercase tracking-[0.3em]">Enregistrer</span>
                    </div>
                </button>
                <button onClick={() => inputRef.current?.click()} className="w-full max-w-[200px] py-5 rounded-2xl bg-white/5 border border-white/10 text-slate-500 font-black uppercase text-[9px]">Fichier Local</button>
            </div>
        )}
        <input type="file" ref={inputRef} onChange={e => e.target.files?.[0] && handlePreScan(e.target.files[0])} className="hidden" accept="audio/*,video/*" />
      </div>

      {isRecordingModalOpen && createPortal(
        <div className="fixed inset-0 z-[2000] bg-black">
           <Recorder 
                mode="video" 
                isTeacher={isTeacher}
                referenceFile={masterReferenceFile} 
                progression={manualGrid.split(/\s+/).filter(t => t.length > 0)}
                onRecordingComplete={(r) => { 
                    const url = registerURLManual(URL.createObjectURL(r.file));
                    setAudioState({ file: r.file, url, metadata: r.metadata });
                    if (!isTeacher) setIsStaged(true); else onFileSelect(r.file, r.metadata);
                    setIsRecordingModalOpen(false);
                }} onCancel={() => setIsRecordingModalOpen(false)} 
            />
        </div>, document.body
      )}
    </div>
  );
};
